/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace */

import {basename, bytesToSize, fromMySQLDate, isset} from '@/util/util';
import {SimConfig}     from './sim.config';
import {RemoteSimJob} from "@/models/sim/remote.sim.job";

/* the kinds of simulation jobs */
export enum SimulationJobType {
  SIMPLE_SIM,
  BATCH_SIM
}

/* the status simulation jobs can have*/
export enum SimulationStatusType {
  NONE,
  FAIL,
  ERROR,
  FAILED,
  CANCELED,
  ABORTED,
  DEATH,
  QUEUED,
  SUCCESS,
  COMPLETED,
  WORKING,
  RUNNING,
  READY,
  CLEANING
}


/**
 *
 * @class SimulationJob
 *
 * @brief base class for simulation "jobs" basically our model for track the launch, progress and completion
 * (or failure) of a simulation in the GUI.  These will be sued for direct user request simulations, i.e. from
 * a blog right click and do a simulation (so this info hangs off of that element in the project), but also
 * for batch simulations, where we are tracking a whole group of MxN combinations of scenario and traffic.
 * In that case the info will hang off of a Simulation element which has dimensions for plant and traffic.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      June 2020
 *
 */
export class SimulationJob {

  /* the kind of simulation */

  public type: SimulationJobType      = SimulationJobType.SIMPLE_SIM;

  /** the job number ("ID") of the remote simulation */
  public jobId: number                = 0;

  /** globally unique id for this job */
  public jobUuid: string              = '';

  /** the current (known) status of the job */
  public status: SimulationStatusType = SimulationStatusType.NONE;

  /** flips to true once the job has resolved **and** downloads are remotely available */
  public resolved: boolean            = false;

  /*
   * will be filled with a truncated version of the stack traceback in the line
   * simulation if we were able to detect a crash.
   *
   */
  public crash: string[]              = [];

  /** when the job was created */
  public expiry: Date                 = null;

  /** when the job was created */
  public created: Date                = null;

  /** when it was removed from teh queue */
  public dequeue: Date                = null;

  /** when it was last updated */
  public updated: Date                = null;

  /** the configuration used to launch the job */
  public  config: SimConfig           = null;

  /** the scenario this simulation is on */
  public      key: string             =  '';

  /** the version of that scenario */
  public instance: string             = 'latest';

  /** the details of the remote simulation job */
  public details: RemoteSimJob|null   = null;

  /** fetch the simulator version (if we have it) */
  public simulator(): string {

    return this.details?.extra?.simulator ?? '';
  }

  public static statusString(value: SimulationStatusType): string {

    switch (value) {

      case SimulationStatusType.NONE:      return 'none';
      case SimulationStatusType.FAIL:      return 'fail';
      case SimulationStatusType.ERROR:     return 'error';
      case SimulationStatusType.FAILED:    return 'failed';
      case SimulationStatusType.ABORTED:   return 'aborted';
      case SimulationStatusType.DEATH:     return 'death';
      case SimulationStatusType.CANCELED:  return 'canceled';
      case SimulationStatusType.QUEUED:    return 'queued';
      case SimulationStatusType.SUCCESS:   return 'success';
      case SimulationStatusType.COMPLETED: return 'completed';
      case SimulationStatusType.WORKING:   return 'working';
      case SimulationStatusType.RUNNING:   return 'running';
      case SimulationStatusType.READY:     return 'ready';
      case SimulationStatusType.CLEANING:  return 'cleaning';
    }

    return 'none';
  }

  /** fetch the icon to use for this kind of artifact */
  public iconOfArtifact(kind: string, missing: boolean): string {

    let prefix: string = "/assets/images/artifacts";

    if (missing) {

      switch (kind) {

        case 'kpi':      return `${prefix}/kpi-missing-32.png`;
        case 'kpix':     return `${prefix}/kpix-missing-32.png`;
        case 'errors':   return `${prefix}/errors-missing-32.png`;
        case 'log':      return `${prefix}/log-missing-32.png`;
        case 'cmd':      return `${prefix}/cmd-missing-32.png`;
        case 'movement': return `${prefix}/movement-missing-32.png`;
        case 'scenario': return `${prefix}/scenario-missing-32.png`;
      }

      return `${prefix}/unknown-missing-32.png`;
    }

    switch (kind) {

      case 'kpi':      return `${prefix}/kpi-32.png`;
      case 'kpix':     return `${prefix}/kpix-32.png`;
      case 'errors':   return `${prefix}/errors-32.png`;
      case 'log':      return `${prefix}/log-32.png`;
      case 'cmd':      return `${prefix}/cmd-32.png`;
      case 'movement': return `${prefix}/movement-32.png`;
      case 'scenario': return `${prefix}/scenario-32.png`;
    }

    return `${prefix}/unknown-32.png`;
  }

  /** fetch the URL for this kind of artifact for this job */
  public artifactURL(kind: string): string {

    return this.artifacts().find(x => x.kind == kind)?.url ?? '';
  }

  public artifactTitle(kind: string): string {

    switch (kind) {

      case 'kpi':      return `KPI`;
      case 'kpix':     return `KPI Extended`;
      case 'errors':   return `Simulation Errors`;
      case 'log':      return `Simulation Log`;
      case 'cmd':      return `Simulation Command`;
      case 'movement': return `Train Movement`;
      case 'scenario': return `Plant Definition / Scenario`;
    }

    return "Unknown Artifact";
  }

  /** fetch the list of artifacts, and leave the URL blank if we don't have that artifact (yet?) */
  public artifacts(): {kind: string, url: string}[] {

    if (this.details) {
      return this.details.artifacts();
    }

    return [
      {
        'kind': 'scenario',
        'url': ''
      },
      {
        'kind': 'movement',
        'url': ''
      },
      {
        'kind': 'cmd',
        'url': ''
      },
      {
        'kind': 'log',
        'url': ''
      },
      {
        'kind': 'errors',
        'url': ''
      },
      {
        'kind': 'kpix',
        'url': ''
      }
    ];
  }

  public updatedFormatted(): string {
    return this.details.ago;
  }

  /** fetch the simulation run time statistics as a formatted string */
  public statsFormatted(): string {

    let rss: string = '';

    if (this.details.last_stats) {

      rss = `, RSS: ${bytesToSize(parseInt(this.details.last_stats.rss))}`;
    }

    return `Sim: ${this.details.runtime.toFixed(2)} sec, Wait: ${this.details.waittime.toFixed(2)} sec, Total: ${this.details.totaltime.toFixed(2)} sec${rss}`;
  }

  /** fetch the simulation name as a formatted string */
  public nameFormatted(reverse: boolean=false): string {

    if (reverse) {

      return `${basename(this.details.name)} - [${this.details.author}] ${this.details.client_id}`;
    }

    return `[${this.details.author}] ${this.details.client_id} - ${basename(this.details.name)}`;
  }

  /** fetch the most recent error (if there is one) and show as a formatted string */
  public errorFormatted(): string {

    return `${this.details.lasterror}`;
  }

  public static statusType(value: string): SimulationStatusType {

    value = value.trim().toLowerCase();

    switch (value) {
      case 'none':      return SimulationStatusType.NONE;
      case 'fail':      return SimulationStatusType.FAIL;
      case 'failed':    return SimulationStatusType.FAILED;
      case 'canceled':  return SimulationStatusType.CANCELED;
      case 'queued':    return SimulationStatusType.QUEUED;
      case 'success':   return SimulationStatusType.SUCCESS;
      case 'complete':  return SimulationStatusType.SUCCESS;
      case 'completed': return SimulationStatusType.SUCCESS;
      case 'working':   return SimulationStatusType.WORKING;
      case 'running':   return SimulationStatusType.WORKING;
      case 'error':     return SimulationStatusType.ERROR;
      case 'aborted':   return SimulationStatusType.ABORTED;
      case 'death':     return SimulationStatusType.DEATH;
      case 'ready':     return SimulationStatusType.READY;
      case 'cleaning':  return SimulationStatusType.CLEANING;
    }

    return SimulationStatusType.NONE;
  }

  /** standard constructor */
  public constructor(init?: Partial<SimulationJob>) {

    this.expiry  = fromMySQLDate("2050-01-01 00:00:00");

    (<any>Object).assign(this, init);
  }

  /**
   * all implementations must return a simlified version of themselves, that is appropriate for use
   * with JSON.stringify().
   */
  toJSON(init?: any): any  {

    const base = {
          type: this.type,
         jobid: this.jobId,
       jobuuid: this.jobUuid,
        status: this.status,
       created: JSON.stringify(this.created),
        expiry: JSON.stringify(this.expiry),
       dequeue: JSON.stringify(this.dequeue),
       updated: JSON.stringify(this.updated),
           key: this.key,
      instance: this.instance,
        config: this.config.toJSON(),
      resolved: this.resolved,
         crash: JSON.stringify(this.crash)
    };

    return init ? {...init, ...base } : base;
  }

  /**
   * all implementations must be able to recover themselves from the given object which was
   * deserialized via JSON.parse().
   */
  fromJSON(json: any) {

    this.type     = json.type;
    this.jobId    = json.jobid;
    this.jobUuid  = json.jobuuid;
    this.status   = json.status;
    this.created  = new Date(JSON.parse(json.created));
    this.dequeue  = new Date(JSON.parse(json.dequeue));
    this.updated  = new Date(JSON.parse(json.updated));
    this.key      = json.key;
    this.instance = json.instance;
    this.resolved = true;

    if (isset(json.expiry)) {

      this.expiry  = new Date(JSON.parse(json.expiry));

    } else {

      this.expiry  = fromMySQLDate("2050-01-01 00:00:00");
    }

    if (isset(json.resolved)) {

      this.resolved = json.resolved;
    }

    this.crash    = [];

    if (isset(json.crash)) {

      this.crash = JSON.parse(json.crash);
    }

    const config  = new SimConfig();
    config.fromJSON(json.config);

    this.config   = config;
  }

  /* fetch the remote id of the job */
  id(): number {

    if (this.jobId < 0) {

      this.jobId *= -1;
    }

    return this.jobId;
  }

  /* fetch the job's globally unique id (within our GUI) */
  uuid(): string {

    return this.jobUuid;
  }

  /* fetch the current status */
  state(): SimulationStatusType {

    if ((this.status != SimulationStatusType.CANCELED) &&
        (this.status != SimulationStatusType.QUEUED) &&
        (this.status != SimulationStatusType.READY)) {

      if (!this.resolved) {

        /*
         * if in the middle of processing and we don't have the remote downloadables yet,
         * then we have to return WORKING, to keep this bastard alive until the downloadable
         * artifacts are ready at the cloud sim service.
         *
         */

        return SimulationStatusType.WORKING;
      }
    }

    return this.status;
  }

  /* do we know for sure it completed ok? */
  complete(): boolean {

    if (!this.resolved) {

      /* can't be confirmed yet...the remote downloadables might not be ready yet. */

      return false;
    }

    return this.isCompleteStatus();
  }

  /* do we know for sure it failed? */
  failed(): boolean {

    if (!this.resolved) {

      /* can't be confirmed yet...the remote downloadables might not be ready yet. */

      return false;
    }

    return this.isFailStatus();
  }

  /* was it canceled? */
  canceled(): boolean {

    return this.status === SimulationStatusType.CANCELED;
  }

  isFailStatus(): boolean {

    switch(this.status) {

      case SimulationStatusType.FAIL:
      case SimulationStatusType.FAILED:
      case SimulationStatusType.ABORTED:
      case SimulationStatusType.CANCELED:
      case SimulationStatusType.ERROR:
      case SimulationStatusType.DEATH:
        return true;
    }

    return false;
  }

  isCompleteStatus(): boolean {

    switch(this.status) {

      case SimulationStatusType.SUCCESS:
      case SimulationStatusType.COMPLETED:
        return true;
    }

    return false;
  }

  /* return an image or span that provides the icon for this job, given its current status */
  icon(): string {

    if (this.isFailStatus()||this.isCompleteStatus()) {

      /*
       * as soon as a step in simulation fails, we know the whole thing has failed, but we may still
       * be waiting for other steps to fail out as well, or we may be waiting for the error logs etc
       * to download.  So don't actually show fail/complete final status, until we are marked as
       * resolved.
       *
       */

      if (!this.resolved) {

        return 'sim-result-working-32.png';
      }

      /* if it finished and resolved, but we detected a crash...than show the job as bombed */

      if (this.crashed()) {

        return 'sim-result-crashed-32.png';
      }

    }

    switch (this.status) {

      case SimulationStatusType.FAIL:      return 'sim-result-fail-32.png';
      case SimulationStatusType.FAILED:    return 'sim-result-fail-32.png';
      case SimulationStatusType.ABORTED:   return 'sim-result-fail-32.png';
      case SimulationStatusType.DEATH:     return 'sim-result-fail-32.png';
      case SimulationStatusType.ERROR:     return 'sim-result-fail-32.png';
      case SimulationStatusType.CANCELED:  return 'sim-result-fail-32.png';
      case SimulationStatusType.QUEUED:    return 'sim-result-queued-32.png';
      case SimulationStatusType.SUCCESS:   return 'sim-result-success-32.png';
      case SimulationStatusType.COMPLETED: return 'sim-result-success-32.png';
      case SimulationStatusType.WORKING:   return 'sim-result-working-32.png';
      case SimulationStatusType.RUNNING:   return 'sim-result-working-32.png';
      case SimulationStatusType.READY:     return 'sim-result-none-32.png';
    }

    return 'sim-result-none-32.png';
  }

  /** quick check to see if the job crashed */
  crashed(): boolean {

    return this.crash.length > 0;
  }

  /** does this job still need to be monitored? */
  active(): boolean {

    if (this.canceled()) {

      /* buh-bye! */

      return false;
    }

    if (!this.resolved) {

      /*
       * we can't actually be failed or complete yet...artifacts might not have been downloaded
       * from Google to cloud simulator proxy
       *
       */

      return true;
    }

    /*
     * if the status was cleaning, or it somehow aborted during cleaning...we should still
     * consider it resolved.  It ran, and it may have failed, we don't know, but the simulator
     * ran.  So as soon as we reach a status of cleaning...its resolved.
     *
     */

    if (this.status == SimulationStatusType.CLEANING) {

      return false;
    }

    return !this.canceled() && !this.failed() && !this.complete();
  }

  /** get a universal/unique key for the job */
  simKey(): string {

    let kind: string = 'linesim';

    if (this.type == SimulationJobType.BATCH_SIM) {

      kind = 'batchsim';
    }

    return `${kind}-job-${this.config.engine}-${this.jobId}`;
  }
}
